import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, X, UserPlus } from "lucide-react";
import { formatDisplayName } from "@/lib/names";
import AddDirectoryPersonDialog from "@/components/crm/AddDirectoryPersonDialog";

const STATUS_META = {
  guest: { label: "Guest", tint: "var(--muted)" },
  disciple: { label: "Disciple", tint: "var(--orange)" },
  member: { label: "Member", tint: "var(--gold)" },
};

// Pick an existing person from the org directory. If they aren't there yet,
// the directory "Add person" form (with duplicate warning) opens inline.
export default function DirectoryPicker({ orgId, title, subtitle, actionLabel = "Add", defaultLifecycle = "guest", onPick, onClose, children, testid = "dir-picker" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/att/promote-candidates", { params: { org_id: orgId, q } })
        .then((r) => setResults(r.data || []))
        .catch(() => toast.error("Search failed."))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [orgId, q]);

  const pick = async (person) => {
    setBusyId(person.id);
    try { await onPick(person); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't add."); }
    finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.55)" }} onClick={onClose} data-testid={`${testid}-overlay`}>
      <div className="w-full max-w-xl rounded-md relative" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="p-5 flex items-start justify-between gap-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <div className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>From the directory</div>
            <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>{title}</h3>
            {subtitle && <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid={`${testid}-close`} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded" style={{ background: "var(--surface-hover)" }}>
            <Search size={16} style={{ color: "var(--muted)" }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, or phone…" className="flex-1 bg-transparent text-sm outline-none" style={{ color: "var(--ink)" }} data-testid={`${testid}-search`} />
          </div>
          {children}
          <div className="space-y-1 max-h-[22rem] overflow-y-auto" data-testid={`${testid}-results`}>
            {loading && <p className="text-sm p-3" style={{ color: "var(--muted)" }}>Searching…</p>}
            {!loading && results.length === 0 && <p className="text-sm p-3" style={{ color: "var(--muted)" }}>No one in the directory matches.</p>}
            {results.map((p) => {
              const meta = STATUS_META[p.lifecycle_status] || STATUS_META.guest;
              return (
                <div key={p.id} className="flex items-center justify-between gap-3 p-3" style={{ borderBottom: "1px solid var(--border-subtle)" }} data-testid={`${testid}-row-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm" style={{ color: "var(--ink)" }}>{formatDisplayName(p)}</span>
                      <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: meta.tint, color: "#FFFFFF" }}>{meta.label}</span>
                    </div>
                    {(p.phone || p.email) && <div className="text-[0.7rem] mt-0.5" style={{ color: "var(--muted)" }}>{p.phone || p.email}</div>}
                  </div>
                  <button onClick={() => pick(p)} disabled={busyId === p.id} className="btn-primary text-xs" style={{ padding: "0.4rem 0.8rem" }} data-testid={`${testid}-pick-${p.id}`}>
                    {busyId === p.id ? "…" : actionLabel}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="pt-3 border-t flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
            <span className="text-xs" style={{ color: "var(--muted)" }}>Not in the directory yet?</span>
            <button onClick={() => setAdding(true)} className="btn-ghost text-xs" data-testid={`${testid}-add-new`}><UserPlus size={13} /> Add to directory</button>
          </div>
        </div>
      </div>
      {adding && (
        <div onClick={(e) => e.stopPropagation()}>
          <AddDirectoryPersonDialog
            orgId={orgId}
            defaultLifecycle={defaultLifecycle}
            initialName={q}
            onClose={() => setAdding(false)}
            onCreated={(p) => { setAdding(false); pick(p); }}
            onUseExisting={(p) => { setAdding(false); pick(p); }}
          />
        </div>
      )}
    </div>
  );
}
